import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { ApprovalSchemeTypeEnum } from '@zaoblako/shared/modules/catalog/enums/approval-scheme-type.enum';
import { ApprovalSchemeTypeEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/approval-scheme-type-enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';

import * as yup from 'yup';

/** Approval scheme create/update request body */
export interface ApprovalSchemeFormInterface {
  /** Schema display name */
  name: string;
  /** Approval scheme type - determines in what order the approval will take place */
  type: ApprovalSchemeTypeEnum;
  /** If `true` then approval scheme will be default for tenant */
  isDefault?: boolean;
  /** Scheme approvers as users list */
  users: UserFindInterface[];
  /** User auditors (default user auditors for entities which use this approval scheme) */
  userAuditors?: UserFindInterface[];
  /** User group auditors (default user group auditors for entities which use this approval scheme) */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Users granted access to this approval scheme */
  userAccess?: UserFindInterface[];
  /** User groups granted access to this approval scheme */
  groupAccess?: UserGroupFindInterface[];
}

/** Approval scheme create/update request body - DTO schema */
export const ApprovalSchemeFormDto = SerdeDtoClass.create<ApprovalSchemeFormInterface>(CatalogDtosKeysEnum.APPROVAL_SCHEME_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  type: {
    label: 'Тип согласования',
    serializer: commonEnumValidator(ApprovalSchemeTypeEnum, ApprovalSchemeTypeEnumI18n),
  },
  isDefault: {
    optional: true,
    label: 'Схема согласования по-умолчанию',
    serializer: yup.boolean(),
  },
  users: {
    label: 'Согласователи',
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    serializer: yup
      .array(entityLikeSchema)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
  },
  userAuditors: {
    optional: true,
    label: 'Пользователи-аудиторы',
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
  userAccess: {
    optional: true,
    label: 'Схема доступна пользователям',
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  groupAccess: {
    optional: true,
    label: 'Схема доступна группам пользователей',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
});
